import { useQuery } from '@tanstack/react-query';

import { getTicketRiskReport } from '~/services/SupportService';

import type { TicketRiskBand } from '~/types/ticketRisk';

export const ticketRiskQueryKeys = {
  all: ['ticketRisk'] as const,
  reports: () => [...ticketRiskQueryKeys.all, 'report'] as const,
  report: (filters: GetTicketRiskReportFilters) => [...ticketRiskQueryKeys.reports(), filters] as const,
  histories: () => [...ticketRiskQueryKeys.all, 'history'] as const,
  history: (ticketId: string, limit?: number) => [...ticketRiskQueryKeys.histories(), ticketId, limit] as const,
  comments: () => [...ticketRiskQueryKeys.all, 'comments'] as const,
  commentThread: (ticketId: string) => [...ticketRiskQueryKeys.comments(), ticketId] as const,
};

interface GetTicketRiskReportFilters {
  band?: TicketRiskBand;
}

interface UseGetTicketRiskReportParams {
  filters?: GetTicketRiskReportFilters;
  /** Skip the request entirely, e.g. for a user who is not allowed to see the dashboard. */
  enabled?: boolean;
}

/**
 * Latest ticket risk scoring run. The run is produced by a batch job, so the data only
 * changes when a new run lands — no point re-fetching on every window focus.
 */
export function useGetTicketRiskReport({ filters = {}, enabled = true }: UseGetTicketRiskReportParams = {}) {
  return useQuery({
    queryKey: ticketRiskQueryKeys.report(filters),
    queryFn: async ({ signal }) => getTicketRiskReport(filters, { signal }),
    enabled,
    refetchOnWindowFocus: false,
    // A 404 means no scoring run exists yet — retrying will not conjure one up.
    retry: (failureCount, error) => ((error as { status?: number }).status === 404 ? false : failureCount < 2),
  });
}
